import { getClassBySize } from '@/utils/modal.utils';
import { twMerge } from 'tailwind-merge';
import { BackButton } from '../Custom/Button/BackButton';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalEvent } from './hooks/event';
import { hideModal } from '@/utils/modal.utils';

type ModalProps = {
  id: string;
  title: string | React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  cancelBtnLabel?: string;
  secondBtnLabel?: string;
  centerY?: boolean;
  showHeaderBackBtn?: boolean;
  showCancelBtn?: boolean;
  secondButtonRef?: React.RefObject<HTMLButtonElement>;
  showSecondBtn?: boolean;
  onClickHeaderBack?: () => void;
  onClickSecondBtn?: () => void;
  onClickCancelBtn?: () => void;
  onClose?: () => void;
  onOpen?: () => void;
  onEscapePress?: () => void;
  onClickOutsideContent?: () => void;
  disabledSubmitButton?: boolean;
  children: React.ReactNode;
  size: 'small' | 'medium' | 'large';
  customCloseButton?: React.ReactNode;
  containerClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
};

const Modal = ({
  id,
  title,
  showHeader = true,
  showFooter = true,
  cancelBtnLabel,
  showHeaderBackBtn,
  showCancelBtn = true,
  secondButtonRef,
  secondBtnLabel,
  showSecondBtn = false,
  centerY = false,
  onClickHeaderBack,
  onClickSecondBtn,
  onClickCancelBtn,
  onClose,
  onOpen,
  onEscapePress,
  onClickOutsideContent,
  disabledSubmitButton,
  children,
  size,
  containerClassName,
  headerClassName,
  bodyClassName,
  customCloseButton,
}: ModalProps) => {
  const { t } = useTranslation('common');
  const modalContentRef = useRef<HTMLDivElement | null>(null);

  useModalEvent(id, modalContentRef, onEscapePress, onOpen);

  return (
    <div id={id} className="modal" data-modal="true">
      <div
        className="bg-transparent fixed top-0 left-0 w-full h-full"
        data-backdrop="true"
        onClick={() => {
          onClickOutsideContent?.();
          hideModal(`#${id}`);
        }}
      />
      <div
        ref={modalContentRef}
        className={twMerge(
          'modal-content overflow-hidden max-w-[600px]',
          centerY ? 'modal-center-y' : 'top-[10%]',
          getClassBySize(size),
          containerClassName
        )}
      >
        {customCloseButton}
        {showHeader && (
          <div
            className={twMerge(
              'modal-header dark bg-[var(--purple-custom)]',
              headerClassName
            )}
          >
            {showHeaderBackBtn && (
              <div className="flex justify-center items-center">
                <BackButton
                  buttonClassName="w-7 min-w-7 h-7 min-h-7"
                  onClick={onClickHeaderBack}
                />
              </div>
            )}
            <h3 className="modal-title">{title}</h3>
            <button
              className="close-button-icon btn btn-xs btn-icon bg-white/15"
              data-modal-dismiss="true"
              onClick={onClose}
            >
              <i className="ki-outline ki-cross text-white"></i>
            </button>
          </div>
        )}
        <div className={twMerge('modal-body m-auto p-8', bodyClassName)}>
          {children}
        </div>
        {showFooter && (
          <div className="modal-footer justify-end">
            <div className="flex gap-4">
              {showCancelBtn && (
                <button
                  className="btn btn-light"
                  data-modal-dismiss="true"
                  onClick={onClickCancelBtn}
                >
                  {cancelBtnLabel || t('common:cancel')}
                </button>
              )}
              {showSecondBtn && (
                <button
                  ref={secondButtonRef}
                  className="btn bg-purple-5433FF text-white"
                  onClick={onClickSecondBtn}
                  data-modal-dismiss="true"
                  disabled={disabledSubmitButton}
                >
                  {secondBtnLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
